import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { log } from 'constants/config';
import { useGeoLocation } from 'hooks/useGeoLocation';
import Button, { ButtonText } from 'components/Button';
import { ADDRESS_INPUT_ID } from 'components/forms/AddressSearch/AddressSearch';
import { RequestLocationPromptProps } from './props';
import { Container, PromptContainer } from './styles';

const RequestLocationPrompt: React.FC<RequestLocationPromptProps> = ({
  children,
  position = 'bottom',
  offset = 8,
  onLocationShared,
  onLocationDenied,
  hideOnShare = true,
  wrapperClassName = '',
}) => {
  const {
    requestUserLocation,
    hasLocationPermission,
    hasPromptedThisSession,
    setHasPromptedThisSession,
  } = useGeoLocation();

  const [showPrompt, setShowPrompt] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);

  const focusListenerRef = useRef<EventListener | null>(null);
  const keyupListenerRef = useRef<EventListener | null>(null);

  const handleShareLocation = async () => {
    try {
      log('Sharing location');

      // Clear any stored location to force fresh request
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.removeItem('bounce_location_data');
      }

      // Always mark as prompted and hide the prompt first
      setHasPromptedThisSession(true);
      if (hideOnShare) {
        setShowPrompt(false);
      }

      // Try to get location (will handle permission dialog if needed)
      await requestUserLocation();
      log('Location shared');

      if (onLocationShared) {
        onLocationShared();
      }
    } catch (error) {
      log('Error sharing location:', error);
      // Already handled above - prompt is closed and session marked
    }
  };

  const handleEnterAddress = () => {
    setShowPrompt(false);
    setHasPromptedThisSession(true);

    if (onLocationDenied) {
      onLocationDenied();
    }
  };

  useEffect(() => {
    const checkLocationPermission = async () => {
      try {
        const permission = await hasLocationPermission();

        if (permission) {
          setHasPromptedThisSession(true);
        } else {
          if (navigator.permissions) {
            navigator.permissions.query({ name: 'geolocation' }).then((permissionStatus) => {
              if (permissionStatus.state === 'denied') {
                setPermissionDenied(true);
                setHasPromptedThisSession(true);
              }
            });
          }
        }
      } catch (error) {
        console.error('Error checking location permission:', error);
      }
    };

    checkLocationPermission();
  }, []);

  useEffect(() => {
    if (permissionDenied) {
      return;
    }

    const handleInputFocus = () => {
      if (!hasPromptedThisSession) {
        setShowPrompt(true);
      }
    };

    const handleInputKeyup = () => {
      if (showPrompt) {
        handleEnterAddress();
      }
    };

    const setupInputObserver = async () => {
      const input = document.getElementById(ADDRESS_INPUT_ID);

      if (input) {
        focusListenerRef.current = handleInputFocus as EventListener;
        input.addEventListener('focus', focusListenerRef.current);

        keyupListenerRef.current = handleInputKeyup as EventListener;
        input.addEventListener('keyup', keyupListenerRef.current);

        return;
      }
    };

    setupInputObserver();

    return () => {
      const input = document.getElementById(ADDRESS_INPUT_ID);
      if (input) {
        // Clean up focus listener
        if (focusListenerRef.current) {
          input.removeEventListener('focus', focusListenerRef.current);
        }

        // Clean up keyup listener
        if (keyupListenerRef.current) {
          input.removeEventListener('keyup', keyupListenerRef.current);
        }
      }
    };
  }, [hasPromptedThisSession, permissionDenied, showPrompt]);

  return (
    <Container className={wrapperClassName}>
      {children}

      {showPrompt && !hasPromptedThisSession && !permissionDenied && (
        <PromptContainer
          position={position}
          offset={offset}
          className="flex w-[13.75rem] flex-col gap-ds-lg rounded-[1.5rem] bg-color-bg-lightmode-primary p-5 shadow-[0px_4px_20px_0px_rgba(0,0,0,0.16)] dark:bg-color-bg-darkmode-primary"
        >
          <span className="typography-product-subheading block">Want results near you?</span>
          <span className="typography-product-caption block text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
            Share your location so we can show you the closest results.
          </span>

          <div className="flex flex-col gap-ds-lg">
            <Button variant="brand" size="md" className="h-[2.6rem]" onClick={handleShareLocation}>
              Share location
            </Button>
            <ButtonText onClick={handleEnterAddress}>Enter an address instead</ButtonText>
          </div>
        </PromptContainer>
      )}
    </Container>
  );
};

export default RequestLocationPrompt;
